import { competitorMatchesSearch } from "./search.js";
import { defineStore } from "pinia";
import {
  COMPETITOR_AUTOMATIC_MATCHING_ALLOW_AUTOMATIC_MATCH_DECISIONS,
  COMPETITOR_DOMAIN_MATCH_MODE_EXACT_HOST,
  COMPETITOR_STATUS_ALLOWED,
} from "@price-rescue/api-contracts";

import { MerchantApiError } from "../authentication/errors.js";
import type {
  CompetitorDomain,
  CompetitorFilters,
  CompetitorFormDraft,
  CompetitorTestResult,
} from "./models.js";
import type { CompetitorsService } from "./service.js";

export const useCompetitorsStore = defineStore("merchant-competitors", {
  state: (): {
    competitors: CompetitorDomain[];
    detail?: CompetitorDomain;
    errorMessage?: string;
    filters: CompetitorFilters;
    formDraft: CompetitorFormDraft;
    loadingDetail: boolean;
    loadingList: boolean;
    mutatingCompetitorId?: string;
    mutationMessage?: string;
    nextCursor: string | null;
    service?: CompetitorsService;
    testResults: Record<string, CompetitorTestResult>;
  } => ({
    competitors: [],
    filters: defaultCompetitorFilters(),
    formDraft: defaultCompetitorFormDraft(),
    loadingDetail: false,
    loadingList: false,
    nextCursor: null,
    testResults: {},
  }),
  actions: {
    configure(service: CompetitorsService): void {
      this.service = service;
    },
    async loadList(filters: CompetitorFilters): Promise<void> {
      this.filters = filters;
      this.loadingList = true;
      delete this.errorMessage;
      try {
        const page = await this.requireService().loadCompetitors(filters);
        this.competitors = page.competitors;
        this.nextCursor = page.nextCursor;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Competitors could not be loaded.");
      } finally {
        this.loadingList = false;
      }
    },
    async loadMore(): Promise<void> {
      if (this.nextCursor === null) return;
      this.loadingList = true;
      delete this.errorMessage;
      try {
        const page = await this.requireService().loadCompetitors({
          ...this.filters,
          cursor: this.nextCursor,
        });
        this.competitors = [...this.competitors, ...page.competitors];
        this.nextCursor = page.nextCursor;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "More competitors could not be loaded.");
      } finally {
        this.loadingList = false;
      }
    },
    async loadDetail(competitorId: string): Promise<void> {
      this.loadingDetail = true;
      delete this.errorMessage;
      try {
        this.detail = await this.requireService().loadCompetitor(competitorId);
        this.formDraft = formDraftFromCompetitor(this.detail);
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Competitor detail could not be loaded.");
      } finally {
        this.loadingDetail = false;
      }
    },
    async createCompetitor(): Promise<void> {
      if (!this.validateCompetitorDraft()) return;
      await this.runCompetitorMutation(
        () => this.requireService().createCompetitor(this.formDraft),
        "Competitor created.",
      );
    },
    async updateCompetitor(): Promise<void> {
      if (this.detail === undefined) return;
      if (!this.validateCompetitorDraft()) return;
      await this.runCompetitorMutation(
        () => this.requireService().updateCompetitor(this.detail!.competitorId, this.formDraft),
        "Competitor updated.",
        this.detail.competitorId,
      );
    },
    async deleteCompetitor(competitorId: string): Promise<void> {
      delete this.errorMessage;
      try {
        this.mutatingCompetitorId = competitorId;
        await this.requireService().deleteCompetitor(competitorId);
        this.competitors = this.competitors.filter((item) => item.competitorId !== competitorId);
        if (this.detail?.competitorId === competitorId) {
          delete this.detail;
        }
        this.mutationMessage = "Competitor deleted.";
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Competitor could not be deleted.");
      } finally {
        delete this.mutatingCompetitorId;
      }
    },
    async testCompetitor(competitorId: string): Promise<void> {
      try {
        this.mutatingCompetitorId = competitorId;
        const result = await this.requireService().testCompetitor(competitorId);
        this.testResults[competitorId] = result;
        this.mutationMessage = result.message;
        await this.loadDetail(competitorId);
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Competitor retrieval test failed to run.");
      } finally {
        delete this.mutatingCompetitorId;
      }
    },
    resetCreateDraft(): void {
      this.clearFeedback();
      this.formDraft = defaultCompetitorFormDraft();
    },
    clearFeedback(): void {
      delete this.errorMessage;
      delete this.mutationMessage;
    },
    async runCompetitorMutation(
      operation: () => Promise<CompetitorDomain>,
      successMessage: string,
      competitorId?: string,
    ): Promise<void> {
      delete this.errorMessage;
      try {
        this.mutatingCompetitorId = competitorId ?? "competitor-form";
        const competitor = await operation();
        this.applyCompetitorIfVisible(competitor);
        this.detail = competitor;
        this.formDraft = formDraftFromCompetitor(competitor);
        this.mutationMessage = successMessage;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Competitor change could not be saved.");
      } finally {
        delete this.mutatingCompetitorId;
      }
    },
    applyCompetitorIfVisible(competitor: CompetitorDomain): void {
      const exists = this.competitors.some((item) => item.competitorId === competitor.competitorId);
      if (!competitorMatchesFilters(competitor, this.filters)) {
        this.competitors = this.competitors.filter(
          (item) => item.competitorId !== competitor.competitorId,
        );
        return;
      }
      this.competitors = exists
        ? this.competitors.map((item) =>
            item.competitorId === competitor.competitorId ? competitor : item,
          )
        : [competitor, ...this.competitors];
    },
    findCompetitor(competitorId: string): CompetitorDomain | undefined {
      const competitors = Array.isArray(this.competitors) ? this.competitors : [];
      return this.detail?.competitorId === competitorId
        ? this.detail
        : competitors.find((item) => item.competitorId === competitorId);
    },
    validateCompetitorDraft(): boolean {
      if (this.formDraft.name.trim().length === 0) {
        this.errorMessage = "Competitor display name is required.";
        return false;
      }
      if (this.formDraft.domain.trim().length === 0) {
        this.errorMessage = "Competitor domain is required.";
        return false;
      }
      delete this.errorMessage;
      return true;
    },
    requireService(): CompetitorsService {
      if (this.service === undefined) {
        throw new Error("Competitors store was not configured before use.");
      }
      return this.service;
    },
  },
});

export function defaultCompetitorFilters(): CompetitorFilters {
  return {
    competitorStatus: COMPETITOR_STATUS_ALLOWED,
    automaticMatching: "ALL",
    limit: 25,
  };
}

export function defaultCompetitorFormDraft(): CompetitorFormDraft {
  return {
    name: "",
    domain: "",
    approvedDomain: true,
    competitorStatus: COMPETITOR_STATUS_ALLOWED,
    automaticMatching: COMPETITOR_AUTOMATIC_MATCHING_ALLOW_AUTOMATIC_MATCH_DECISIONS,
    domainMatchMode: COMPETITOR_DOMAIN_MATCH_MODE_EXACT_HOST,
    approvedSubdomainPatterns: [],
    notes: "",
  };
}

function formDraftFromCompetitor(competitor: CompetitorDomain): CompetitorFormDraft {
  return {
    name: competitor.name,
    domain: competitor.normalizedHost,
    approvedDomain: competitor.approvedDomain,
    competitorStatus: competitor.competitorStatus,
    automaticMatching: competitor.automaticMatching,
    domainMatchMode: competitor.domainMatchMode,
    approvedSubdomainPatterns: competitor.approvedSubdomainPatterns,
    notes: competitor.notes ?? "",
  };
}

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.code === "REQUEST_DUPLICATE")
    return "That competitor domain already exists.";
  if (error instanceof MerchantApiError && error.code.startsWith("VALIDATION_"))
    return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function competitorMatchesFilters(
  competitor: CompetitorDomain,
  filters: CompetitorFilters,
): boolean {
  return (
    matchesSearchFilter(competitor, filters) &&
    matchesStatusFilter(competitor, filters) &&
    matchesAutomaticMatchingFilter(competitor, filters)
  );
}

function matchesSearchFilter(competitor: CompetitorDomain, filters: CompetitorFilters): boolean {
  return competitorMatchesSearch(competitor, filters.search);
}

function matchesStatusFilter(competitor: CompetitorDomain, filters: CompetitorFilters): boolean {
  return (
    filters.competitorStatus === undefined ||
    filters.competitorStatus === "ALL" ||
    competitor.competitorStatus === filters.competitorStatus
  );
}

function matchesAutomaticMatchingFilter(
  competitor: CompetitorDomain,
  filters: CompetitorFilters,
): boolean {
  return (
    filters.automaticMatching === undefined ||
    filters.automaticMatching === "ALL" ||
    competitor.automaticMatching === filters.automaticMatching
  );
}
